/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Handling webhooks", description = "Use functions to reshape webhook payloads before Hightouch writes them to your warehouse or sends them to destinations.", lastUpdatedDate = new Date(1782345600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Handling webhooks",
        description: "Use functions to reshape webhook payloads before Hightouch writes them to your warehouse or sends them to destinations.",
        lastUpdatedDate: "Thu Jun 25 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: []
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      pre: "pre",
      code: "code",
      span: "span"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "Use functions to reshape webhook payloads before Hightouch writes them to your warehouse or sends them to destinations."
      }), "\n", _jsx(_components.p, {
        children: "These webhooks usually contain event data that are helpful for building a full\nprofile of your users. For example, Shopify has webhooks for when users check\nout, and Iterable has webhooks for when users open emails."
      }), "\n", _jsxs(_components.p, {
        children: ["To set this up, create a ", _jsx(_components.a, {
          href: "/events/sources/webhook",
          children: "webhook source"
        }), "\nand add it to the tool that emits events. You can then process the raw webhook\ndata using functions."]
      }), "\n", _jsx(_components.p, {
        children: "For example, your code might look something like:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-js",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "async"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "function"
          }), " ", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "transformEvents"
          }), "(", _jsx(_components.span, {
            className: "hljs-params",
            children: "events"
          }), ") {\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "return"
          }), " events.", _jsx(_components.span, {
            className: "hljs-title function_",
            children: "map"
          }), "(", _jsxs(_components.span, {
            className: "hljs-function",
            children: ["(", _jsx(_components.span, {
              className: "hljs-params",
              children: "event"
            }), ") =>"]
          }), " ({\n    ...event,\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "type"
          }), ": ", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"track\""
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "event"
          }), ": event.", _jsx(_components.span, {
            className: "hljs-property",
            children: "event"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "messageId"
          }), ": event.", _jsx(_components.span, {
            className: "hljs-property",
            children: "messageId"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "properties"
          }), ": {\n      ...event.", _jsx(_components.span, {
            className: "hljs-property",
            children: "properties"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "dataFields"
          }), ",\n      ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "email"
          }), ": event.", _jsx(_components.span, {
            className: "hljs-property",
            children: "properties"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "email"
          }), ",\n    },\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "anonymousId"
          }), ": event.", _jsx(_components.span, {
            className: "hljs-property",
            children: "properties"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "userId"
          }), "\n      ? ", _jsx(_components.span, {
            className: "hljs-literal",
            children: "null"
          }), "\n      : event.", _jsx(_components.span, {
            className: "hljs-property",
            children: "properties"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "email"
          }), ",\n    ", _jsx(_components.span, {
            className: "hljs-attr",
            children: "userId"
          }), ": event.", _jsx(_components.span, {
            className: "hljs-property",
            children: "properties"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "body"
          }), ".", _jsx(_components.span, {
            className: "hljs-property",
            children: "userId"
          }), ",\n  }));\n}\n"]
        })
      })]
    });
  }
}
export default MDXContent;
